// Desktop header (≥1100px): wordmark · LOG PLAN TRENDS · weight status ·
// ◆ COACH · data-health / pending-writes pills · ⚙. Replaces the old TopBar +
// DesktopSlimHeader.

import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { C, FONT_DISPLAY, FONT_MONO } from '../../lib/design';
import {
  computeWeeklyRate,
  filterFromStart,
  programWeek,
  useCoachLatest,
  useProfile,
} from '../../lib/day-data';
import { fmt1, fmtDelta } from '../../lib/ledger';
import { useLedger } from '../../lib/ledger-state';
import { DEFAULT_PROFILE, WeightRowsSchema, type WeightRow } from '../../lib/types';
import { STALE_TIME_DAILY } from '../../lib/query';
import { DataHealthPill } from '../DataHealthPill';
import { PendingWritesPill } from '../PendingWritesPill';
import { Sub } from './ui';

const NAV: Array<{ to: '/log' | '/plan' | '/trends'; label: string }> = [
  { to: '/log', label: 'LOG' },
  { to: '/plan', label: 'PLAN' },
  { to: '/trends', label: 'TRENDS' },
];

/** Live weight status for the header: current kg, weekly rate, kg to goal,
 *  program week. */
export function useHeaderStatus() {
  const profile = useProfile().data ?? DEFAULT_PROFILE;
  const weightsQuery = useQuery<WeightRow[]>({
    queryKey: ['weights'],
    queryFn: () => apiClient.get<WeightRow[]>('/api/weights', { schema: WeightRowsSchema }),
    staleTime: STALE_TIME_DAILY,
  });
  const sorted = filterFromStart(
    [...(weightsQuery.data ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
    profile.start_date,
  );
  const current = sorted.length ? sorted[sorted.length - 1]!.weight_kg : profile.weight_start_kg ?? 0;
  const rate = computeWeeklyRate(sorted);
  const goal = profile.goal_weight_kg ?? DEFAULT_PROFILE.goal_weight_kg!;
  return { current, rate, toGoal: current - goal, week: programWeek(profile), goal };
}

/** Pending coach suggestions (drives the ◆ COACH count on both platforms). */
export function useCoachPendingCount(): number {
  const latest = useCoachLatest();
  return (latest.data?.suggestions ?? []).filter((s) => s.status === 'pending').length;
}

export function Header() {
  const path = useRouterState().location.pathname;
  const status = useHeaderStatus();
  const pending = useCoachPendingCount();
  const ledger = useLedger();
  return (
    <div
      style={{
        borderBottom: `1px solid ${C.line}`,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        padding: '0 24px',
        minHeight: 64,
        background: C.bg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
        <Link to="/log" style={{ fontFamily: FONT_DISPLAY, fontSize: 22, letterSpacing: '-0.02em', color: C.text }}>
          <span style={{ fontWeight: 300, fontStyle: 'italic' }}>the</span> protocol
        </Link>
        <nav style={{ display: 'flex' }} aria-label="Primary">
          {NAV.map((n) => {
            const active = path === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                style={{
                  display: 'block',
                  borderBottom: `2px solid ${active ? C.signal : 'transparent'}`,
                  color: active ? C.signal : C.dim,
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  letterSpacing: '0.2em',
                  padding: '22px 18px 20px',
                }}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
        <Sub className="num">
          <span style={{ color: C.text }}>{fmt1(status.current)}</span> KG ·{' '}
          <span style={{ color: status.rate <= 0 ? C.signal : C.warn }}>{fmtDelta(status.rate)}</span>{' '}
          /WK · {fmt1(Math.max(0, status.toGoal))} TO GOAL · WK {status.week}
        </Sub>
        <button
          type="button"
          onClick={ledger.toggleCoach}
          aria-pressed={ledger.coach}
          style={{
            background: ledger.coach ? C.panel2 : 'transparent',
            border: `1px solid ${pending ? C.signal : C.line}`,
            color: pending ? C.signal : C.dim,
            fontFamily: FONT_MONO,
            fontSize: 10,
            letterSpacing: '0.15em',
            padding: '0 10px',
            height: 28,
            cursor: 'pointer',
          }}
        >
          ◆ COACH{pending ? ` · ${pending}` : ''}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DataHealthPill compactWhenHealthy />
          <PendingWritesPill />
        </div>
        <Link
          to="/settings"
          aria-label="Settings"
          title="Settings"
          style={{
            color: path === '/settings' ? C.text : C.dim,
            fontSize: 18,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ⚙
        </Link>
      </div>
    </div>
  );
}
